import type { RawEventData } from '@fullstory/recording-api';
import type {
  ApiV1,
  ApiV2,
  V1OperationOption,
  V2Operation,
  V2OperationOptions,
} from '@fullstory/browser-public-api';
import { Globals, GlobalsOnWindowLike, getGlobalsOn, getNamespace } from './window';

/** The FullStory Recording API v1 */
export interface BrowserV1Api extends ApiV1, PrivateApi, DeprecatedPrivateApi {}

/** Api v1 all recording operations */
export namespace V1OperationName {
  /** An external API used to set user consent state. (Generates SYS_SETCONSENT.) */
  export const Consent = 'consent';
  /** An external API used to implement FS.log(). (Generates SYS_LOG.) */
  export const Log = 'log';
  /** An internal API used to implement FS.shutdown() and FS.restart(). */
  export const Record = 'rec';
}

/** Api v1 operations specifically to support customEvents and User|Page vars
 * @see {vars.ts}
 */
export namespace VarOperationName {
  /** An external API used to create a custom event. (Generates SYS_CUSTOM.)  */
  export const Event = 'event';
  /** An external API used to set page-scoped variables. (Generates SYS_SETVAR.) */
  export const Page = 'page';
  /**
   * An external API used to set variables of arbitrary scope. (Generates
   * SYS_SETVAR.)
   * TODO(sethf): Should we fold 'page' into this? We're still able to do so,
   * since we haven't publicly shipped it yet.
   */
  export const Vars = 'setVars';
  /** The external API used to set user-scoped variables. (Generates SYS_SETVAR.) */
  export const User = 'user';
}

/** Api v1 all available operations */
export type V1Operation = (typeof V1OperationName)[keyof typeof V1OperationName] | VarOperation;

/** Api v1 all available custom/user/page var operations */
export type VarOperation = (typeof VarOperationName)[keyof typeof VarOperationName];

/**
 * @deprecated
 * An obsolete external API that was previously used for user-scoped
 * variables. This is rejected by the server and has been silently dropped by
 * {@link buildVarRawEvent} for some time now, so support for it has been
 * removed. We should avoid reusing the name.
 */
export type DeprecatedOperation = 'account';
export type ApiVersion = number;

export type Source = string;
export type BrowserApiCall<Op extends V2Operation = V2Operation> =
  | [V1Operation, V1OperationOption, Source?, ResolveOperation?, RejectOperation?, ApiVersion?]
  | [Op, V2OperationOptions[Op], Source?, ResolveOperation?, RejectOperation?, ApiVersion?];

/** @deprecated - used by integrations */
type Cookies = { [key: string]: string };

/** Internal API functions - all undocumented and considered experimental. */
interface PrivateApi {
  q?: BrowserApiCall[];
  _fs_loaded?: boolean;
  _v?: string;
  _w?: Partial<Window>;
  // Denotes whether we are in a native mobile WebView
  _native?: boolean;
  _api(
    operation: V1Operation | V2Operation,
    options?: V1OperationOption,
    source?: string,
    resolve?: ResolveOperation,
    reject?: RejectOperation,
  ): void;
  _withEventQueue(
    callerSignature: string | null,
    callback: (q: EventQueueLike | null) => void,
  ): void;
  _withRecorder(callerSignature: string | undefined, callback: (q: RecorderLike) => void): void;
  /** This is implemented via an integration script */
  _init_in_session(args: InitInSessionArgs): void;
  _init_callback?: () => void;
  dataLayer?: any;
}

/** @ts-ignore - Old discarded public APIs */
interface DeprecatedPublicApi {
  /** @deprecated  */
  clearUserCookie(): void;
  /** @deprecated  */
  identifyAccount(id: string, customVars?: V1OperationOption): void;
}

/**
 * Old discarded private APIs that still may have references in our
 * integration scripts to clean up before we can remove.
 */
interface DeprecatedPrivateApi {
  /** @deprecated - only used in integrations */
  _cookies(): Cookies | null;
  /** @deprecated - only used in integrations */
  _setCookie(key: string, value: string): void;
}

export interface EventQueueLike {
  enqueue(eventData: RawEventData): void;
  enqueueFirst(eventData: RawEventData): void;
}

// todo(clint) this should be a proper Envelope
export interface RecorderLike {
  onMessageReceived(source: WindowProxy | null, msg: any[]): void;
  onNavigate(): void;
}

/**
 * Initialization arguments for the _init_in_session API.
 * This is an interface to allow for easy iteration of the
 * api. Note, this needs to match SnippetArgs in
 * fs/services/emissary/internal/integrations/fsinsession/tsexport.go
 * This is intentionally different to prevent pulling in too many
 * dependencies into the browser-api package.
 */
export interface InitInSessionArgs {
  predictIntervalBundles: number;
  enableDataLayerForEveryone: string;
  enablePredictionsForEveryone: string;
  // to sample prediction API traffic, should be an integer between 0 and 100
  samplingRate: number;
  // pushPredictionToOptimizely determines whether to push prediction class to Optimizely as a custom user attribute.
  pushPredictionToOptimizely: string;
  // pushPredictionToSessionAI determines whether to push prediction score to SessionAI as an event.
  pushPredictionToSessionAI: string;
}

/** API v2 defined as a callable interface */
// TODO: this needs to extend private and deprecated interfaces.
export interface BrowserV2Api extends ApiV2, PrivateApi, DeprecatedPrivateApi {}

/** Async api callbacks */
export type ResolveOperation<T = any> = (value: T) => T | PromiseLike<T>;
export type RejectOperation<T = any> = (reason: T) => T | PromiseLike<T>;

/** FS Api v1.x */
export function getBrowserApi(
  wnd: GlobalsOnWindowLike,
  namespace: string = getNamespace(wnd),
): BrowserV1Api | undefined {
  return (wnd as any)[namespace];
}

/** FS Api v2.x */
export function getRecordingApi(
  wnd: GlobalsOnWindowLike,
  namespace: string = getNamespace(wnd),
): BrowserV2Api | undefined {
  return (wnd as any)[namespace];
}

/**
 * A helper type to make it more convenient to write TypeScript code that
 * interacts with the FullStory browser API when you know the namespace you
 * intended to use at compile time.
 */
type GlobalsNS<NS extends string> = Globals & {
  [NSName in NS]: BrowserV1Api;
};

type GlobalsOnNS<NS extends string, Wnd extends WindowOrWorkerGlobalScope> = GlobalsNS<NS> & Wnd;

/**
 * Returns the FullStory globals we expose on 'window', with the browser API
 * available under the provided namespace.
 */
export function getGlobalsOnNS<NS extends string, Wnd extends WindowOrWorkerGlobalScope>(
  ns: NS,
  wnd: Wnd,
): GlobalsOnNS<NS, Wnd> | undefined {
  return getGlobalsOn(wnd) as GlobalsOnNS<NS, Wnd> | undefined;
}
